import { getGameHandler } from "@/games";
import type { CardItem } from "@/interfaces/CardItem";
import type { CardOrientation } from "@/interfaces/GameHandler";
import type { SupportedGames } from "@/types/game";

/**
 * Laying out card art that is not all the same way up.
 *
 * Most cards are portrait. A game may print some of its cards sideways --
 * Palworld's Structures -- and those are stored as they are printed, so any
 * component that reserves a card-shaped box has to know which it is holding.
 *
 * The rule everywhere: a sideways card keeps a full portrait slot, so grids and
 * deck zones stay on one rhythm, and its art fills the slot's width and sits
 * across the middle of it. Turning it upright instead would leave its text
 * running up the side.
 */

/** The stored art's dimensions, portrait. A landscape card swaps them. */
export const CARD_ART_WIDTH = 316;
export const CARD_ART_HEIGHT = 440;

export const CARD_ASPECT_RATIO = `${CARD_ART_WIDTH} / ${CARD_ART_HEIGHT}`;
export const LANDSCAPE_CARD_ASPECT_RATIO = `${CARD_ART_HEIGHT} / ${CARD_ART_WIDTH}`;

/**
 * The orientation of the card behind a deck builder tile.
 *
 * Deck builder components identify a card by its alt-art id and know only which
 * game they are in, so this resolves the card and asks its game. Games that
 * never print sideways answer "portrait" without having to say anything.
 */
export function orientationOf(
  game: SupportedGames,
  aaCardId: string,
): CardOrientation {
  if (!aaCardId) return "portrait";

  const handler = getGameHandler(game);
  const card = handler.getModalCardData(aaCardId);

  if (!card) return "portrait";
  return handler.getCardOrientation?.(card) ?? "portrait";
}

/** As above, when the caller already has the card. */
export function orientationOfCard(
  game: SupportedGames,
  card: CardItem | undefined,
): CardOrientation {
  if (!card) return "portrait";
  return getGameHandler(game).getCardOrientation?.(card) ?? "portrait";
}

export interface ArtSize {
  width: number;
  height: number;
}

/**
 * The size art renders at inside a portrait slot.
 *
 * A portrait card fills the slot. A landscape card is as wide as the slot and
 * as tall as the stored art's ratio makes it, which is a little over half the
 * slot -- the rest is the space above and below it.
 */
export function artSizeIn(
  slotWidth: number,
  slotHeight: number,
  orientation: CardOrientation,
): ArtSize {
  if (orientation !== "landscape") {
    return { width: slotWidth, height: slotHeight };
  }

  return {
    width: slotWidth,
    height: Math.round(slotWidth * (CARD_ART_WIDTH / CARD_ART_HEIGHT)),
  };
}
